import { toZonedTime } from 'date-fns-tz'
import { TFunction } from 'i18next'
import z from 'zod'

import { getPersonColumn, PersonColumnSettings } from '@/components/business/person-column'
import { Badge } from '@/components/ui/badge'
import { DEFAULT_LIMIT } from '@/components/ui/pagination/pagination.constants'
import { SelectOption } from '@/components/ui/select'
import { SortOrderSchema, TableColumn } from '@/components/ui/table'
import { EMPTY_VALUE } from '@/constants'
import { ClientModel } from '@/models'
import { formatDateLong, formatDateOnly, formatTimeOnly } from '@/utils/date'

import { getClientStatusColor } from './client-table.page.utils'
import type { AppUserFilter } from './clients-table.types'
import {
  AppUserFilterEnum,
  ClientFilterStatusEnum,
  NonPMSConnectedBuildingStatusEnum,
  PMSConnectedBuildingStatusEnum,
} from './clients-table.types'

export const APP_USER_CUTOFF_DATE_MS = new Date('2025-01-01').getTime()

export const CLIENTS_ID = 'clients'

export const CLIENTS_PMS_CONNECTED_ID = 'clients-pms-connected'

export const getClientsTableColumns = (
  t: TFunction<['clients', 'common']>,
  timezone: string,
  isPMSConnectedBuilding: boolean,
  showAppUserColumns: boolean,
  residentColumnSettings: PersonColumnSettings,
): TableColumn<ClientModel>[] => {
  const columns: TableColumn<ClientModel>[] = [
    getPersonColumn(residentColumnSettings, {
      getPerson: (client) => ({
        apartment: client.apartment,
        avatar: client.user.avatar,
        buildingNumber: client.buildingNumber,
        clientId: client.id,
        email: client.user.email,
        firstName: client.user.firstName,
        kind: 'resident',
        lastName: client.user.lastName,
        phone: client.phone,
        street: client.street,
      }),
      id: 'name',
      pageName: t('clients:list.title'),
      sortKey: 'fullName',
      source: 'contacts',
      title: t('clients:list.columns.resident'),
    }),
    {
      id: 'unit',
      render: (client) => client.apartment || EMPTY_VALUE,
      sortKey: 'apartment',
      title: t('clients:list.columns.unit'),
      width: '20rem',
    },
    {
      hiddenByDefault: !isPMSConnectedBuilding,
      id: 'status',
      render: (client) => {
        const status = client.statusPMS?.status

        if (!status) {
          return EMPTY_VALUE
        }

        return (
          <Badge color={getClientStatusColor(status)} variant="light" size="small">
            {status}
          </Badge>
        )
      },
      title: t('clients:list.columns.status'),
      width: '14rem',
    },
    {
      id: 'email',
      render: (client) => client.user.email || EMPTY_VALUE,
      title: t('clients:list.columns.email'),
      width: '24rem',
    },
    {
      id: 'moveIn',
      render: (client) => {
        if (!client.moveInDate) {
          return EMPTY_VALUE
        }

        return formatDateLong(toZonedTime(client.moveInDate, timezone))
      },
      sortKey: 'moveInDate',
      title: t('clients:list.columns.moveIn'),
      width: '14rem',
    },
    {
      id: 'moveOut',
      render: (client) => {
        if (!client.moveOutDate) {
          return EMPTY_VALUE
        }

        return formatDateLong(toZonedTime(client.moveOutDate, timezone))
      },
      sortKey: 'moveOutDate',
      title: t('clients:list.columns.moveOut'),
      width: '14rem',
    },
    {
      id: 'leaseStart',
      render: (client) => {
        if (!client.leaseStart) {
          return EMPTY_VALUE
        }

        return formatDateLong(toZonedTime(client.leaseStart, timezone))
      },
      sortKey: 'leaseStart',
      title: t('clients:list.columns.leaseStart'),
      width: '14rem',
    },
    {
      id: 'leaseEnd',
      render: (client) => {
        if (!client.leaseEnd) {
          return EMPTY_VALUE
        }

        return formatDateLong(toZonedTime(client.leaseEnd, timezone))
      },
      sortKey: 'leaseEnd',
      title: t('clients:list.columns.leaseEnd'),
      width: '14rem',
    },
    {
      hiddenByDefault: true,
      id: 'phone',
      render: (client) => client.phone || EMPTY_VALUE,
      title: t('clients:list.columns.phone'),
      width: '18rem',
    },
    {
      hiddenByDefault: true,
      id: 'buildingName',
      render: (client) => client.buildingId?.name || EMPTY_VALUE,
      title: t('clients:list.columns.buildingName'),
      width: '18rem',
    },
    {
      hiddenByDefault: true,
      id: 'created',
      render: (client) => {
        if (!client.user) {
          return EMPTY_VALUE
        }

        return `${formatDateOnly(toZonedTime(client.user.createdAt, timezone))}\n${formatTimeOnly(toZonedTime(client.user.createdAt, timezone))}`
      },
      sortKey: 'createdAt',
      title: t('clients:list.columns.created'),
      width: '14rem',
    },
  ]

  // App User columns are gated behind the building's App User Status feature flag. Building
  // the list conditionally — rather than filtering it afterward by column title —
  // keeps column identity out of the now-translated display text.
  if (showAppUserColumns) {
    columns.push(
      {
        hiddenByDefault: true,
        id: 'appUser',
        render: (client) => (
          <Badge color={client.isAppUser ? 'green' : 'red'} variant="light" size="small">
            {client.isAppUser ? t('clients:list.appUser.yes') : t('clients:list.appUser.no')}
          </Badge>
        ),
        title: t('clients:list.columns.appUser'),
        width: '10rem',
      },
      {
        hiddenByDefault: true,
        id: 'lastAppLogin',
        render: (client) => {
          if (!client.lastAppLogin || client.lastAppLogin < APP_USER_CUTOFF_DATE_MS) return '-'
          return formatDateLong(toZonedTime(client.lastAppLogin, timezone))
        },
        title: t('clients:list.columns.lastAppLogin'),
        width: '14rem',
      },
    )
  }

  return columns
}

export const PMS_ALLOWED_STATUSES = PMSConnectedBuildingStatusEnum.options.filter(
  (s) => s !== PMSConnectedBuildingStatusEnum.enum.all,
)

export const NON_PMS_ALLOWED_STATUSES = NonPMSConnectedBuildingStatusEnum.options

export const getAppUserOptions = (
  t: TFunction<['clients', 'common']>,
): SelectOption<AppUserFilter>[] => [
  { label: t('clients:list.appUser.yes'), value: 'yes' },
  { label: t('clients:list.appUser.no'), value: 'no' },
]

export const clientsTableInitialQueryFiltersSchema = z.object({
  appUser: z.array(AppUserFilterEnum).optional(),
  limit: z.coerce.number().optional(),
  search: z.coerce.string().optional(),
  skip: z.coerce.number().optional(),
  sortBy: z.string().optional(),
  sortOrder: SortOrderSchema.optional(),
  status: ClientFilterStatusEnum.optional(),
})

export const clientsTableInitialFilters: z.infer<typeof clientsTableInitialQueryFiltersSchema> = {
  appUser: undefined,
  limit: DEFAULT_LIMIT,
  search: '',
  skip: 0,
  // Note: Initial "status" value is added by a useEffect in clients-table component
}
